import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Platform, Animated, Easing } from 'react-native';
import { useIconTheme } from '../contexts/IconThemeContext';
import { getIconForCategory, DEFAULT_ICON_SET } from '../config/icons';

// Legacy function for backward compatibility - uses default icon set
// Components should prefer using the useIconTheme hook for dynamic theming
const getCategoryIcon = (category) => {
  return getIconForCategory(category, DEFAULT_ICON_SET);
};

/**
 * Infer display category from hotspot content when stored category is OTHER or wrong.
 * e.g. name/description/address/tags contain "library" -> show book icon (LIBRARY)
 */
const inferCategoryForIcon = (hotspot) => {
  const raw = (hotspot?.category || hotspot?.category_name || 'OTHER').toString().toUpperCase();
  if (raw === 'LIBRARY') return 'LIBRARY';

  const text = [
    hotspot?.name || '',
    hotspot?.description || '',
    hotspot?.address || '',
    (hotspot?.tags || []).join(' '),
  ].join(' ').toLowerCase();

  if (/\blibrar(y|ies)\b/.test(text)) return 'LIBRARY';
  return raw || 'OTHER';
};

// Inject floating animation CSS once
const injectFloatingAnimation = () => {
  if (typeof document !== 'undefined' && !document.getElementById('marker-float-animation')) {
    const style = document.createElement('style');
    style.id = 'marker-float-animation';
    style.textContent = `
      @keyframes markerFloat {
        0%, 100% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-6px);
        }
      }
      @keyframes shadowFadeIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes shadowPulse {
        0%, 100% {
          transform: translateX(-50%) scale(1);
          opacity: 0.3;
        }
        50% {
          transform: translateX(-50%) scale(0.7);
          opacity: 0.2;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

// Scale prop controls marker size: 1.0 = base size (smallest), 1.25x = largest (HIGH activity)
// isVerified shows a checkmark badge for CONFIRMED hotspots
// activityLevel determines marker color intensity (HIGH = more vibrant)
const HotspotMarker = ({ hotspot, onClick, scale = 1.0, isVerified = false, activityLevel = 'MEDIUM', showShadowAnimation = true }) => {
  // Use the icon theme context for dynamic icon theming
  const { getCategoryIcon: getThemedIcon } = useIconTheme();
  
  if (Platform.OS === 'web') {
    // Inject animation CSS on mount
    useEffect(() => {
      injectFloatingAnimation();
    }, []);

    // Generate a random animation delay for variety
    const animationDelay = useRef(Math.random() * 2).current;
    
    // Base dimensions - scaled by activity level
    const baseWidth = 66;
    const baseHeight = 90;
    const scaledWidth = Math.round(baseWidth * scale);
    const scaledHeight = Math.round(baseHeight * scale);
    
    // Scale icon and shadow proportionally
    const baseFontSize = 20;
    const scaledFontSize = Math.round(baseFontSize * scale);
    const baseIconSize = 24;
    const scaledIconSize = Math.round(baseIconSize * scale);
    const baseShadowWidth = 40;
    const scaledShadowWidth = Math.round(baseShadowWidth * scale);
    const baseShadowHeight = 12;
    const scaledShadowHeight = Math.round(baseShadowHeight * scale);

    // Get marker color based on activity level
    // HIGH = vibrant red, MEDIUM = standard red, LOW = muted red
    const getMarkerColor = () => {
      const level = (activityLevel || 'MEDIUM').toUpperCase();
      const colors = {
        'HIGH': '#D32F2F',    // Vibrant red for high activity
        'MEDIUM': '#E53935',  // Standard red
        'LOW': '#EF5350',     // Lighter/muted red for low activity
      };
      return colors[level] || colors['MEDIUM'];
    };
    
    const markerColor = getMarkerColor();

    // For web, we use this component with OverlayView in MapView
    // Using same pin shape as player/club markers but in red
    return (
      <div
        onClick={onClick}
        style={{
          position: 'relative',
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          cursor: 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          transformOrigin: 'bottom center',
        }}
      >
        {/* Floating pin container */}
        <div
          style={{
            animation: `markerFloat 3s ease-in-out infinite`,
            animationDelay: `${animationDelay}s`,
          }}
        >
          {/* Pin SVG with inner circle for category icon - color based on activity level */}
          <svg 
            width={scaledWidth} 
            height={scaledHeight} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 44 60" 
            style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
            }}
          >
            {/* Base pin color */}
            <path 
              d="M 22 0 C 13 0 6 7 6 16 C 6 22 9 27 22 52 C 35 27 38 22 38 16 C 38 7 31 0 22 0 Z" 
              fill={markerColor} 
              stroke="none"
            />
            {/* White circle for icon background */}
            <circle cx="22" cy="16" r="11" fill="white" fillOpacity="0.95"/>
          </svg>
          
          {/* Verified badge - shown for CONFIRMED hotspots */}
          {isVerified && (
            <div
              style={{
                position: 'absolute',
                top: `${Math.round(-4 * scale)}px`,
                right: `${Math.round(2 * scale)}px`,
                width: `${Math.round(20 * scale)}px`,
                height: `${Math.round(20 * scale)}px`,
                backgroundColor: '#4CAF50',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `${Math.round(2 * scale)}px solid white`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                zIndex: 1,
              }}
            >
              <span style={{ 
                color: 'white', 
                fontSize: `${Math.round(12 * scale)}px`,
                fontWeight: 'bold',
                lineHeight: 1,
              }}>✓</span>
            </div>
          )}
          
          {/* Category Icon - positioned at center of white circle */}
          <div
            style={{
              position: 'absolute',
              top: `${Math.round(12 * scale)}px`, // Adjusted to center on the circle, scaled
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: `${scaledFontSize}px`,
              width: `${scaledIconSize}px`,
              height: `${scaledIconSize}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {getThemedIcon(inferCategoryForIcon(hotspot))}
          </div>
        </div>
        
        {/* Sharp Oval Shadow Below Marker - only animate at higher zoom to avoid clutter when zoomed out */}
        {showShadowAnimation && (
        <div
          style={{
            position: 'absolute',
            bottom: `${Math.round(-8 * scale)}px`, // Below the marker, scaled
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${scaledShadowWidth}px`,
            height: `${scaledShadowHeight}px`,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: -1,
            animation: `shadowFadeIn 120ms ease-out forwards, shadowPulse 3s ease-in-out 120ms infinite`,
            animationDelay: `0s, ${animationDelay}s`,
          }}
        />
        )}
      </div>
    );
  }

  // For mobile (React Native), use View components
  return (
    <View style={styles.markerContainer}>
      <View style={styles.pulse} />
      <View style={styles.pin}>
        <View style={styles.iconContainer}>
          <View style={styles.icon}>
            {/* For mobile, we'd use an Icon component here */}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  markerContainer: {
    position: 'relative',
    alignItems: 'center',
  },
  pulse: {
    position: 'absolute',
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: 'rgba(229, 57, 53, 0.3)',
  },
  pin: {
    width: 66,
    height: 90,
    backgroundColor: '#E53935',
    borderRadius: 33,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'white',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: 24,
  },
});

export default HotspotMarker;
export { getCategoryIcon };
